import { Link } from "react-router-dom";
import { postsByLang, formatPostDate } from "@/data/posts";
import { useLocale } from "@/i18n/useLocale";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { useGsapReveal } from "@/hooks/useGsapReveal";
import { useCarousel } from "@/hooks/useCarousel";
import "./NewsSection.css";

/**
 * Ultimas novedades en la home, en carrusel (una linea, 4 tarjetas visibles).
 *
 * El carrusel es scroll nativo con scroll-snap, no una libreria: asi funciona
 * con el dedo, con el trackpad y con el teclado sin codigo propio, y el scroll
 * de la pagina nunca queda secuestrado. Los botones son un atajo para mouse,
 * no el unico modo de moverse.
 *
 * Si no hay notas no renderiza nada: mejor que la seccion no exista a mostrar
 * un hueco vacio.
 */
function NewsSection() {
  const { t, locale, path } = useLocale();
  const headerRef = useScrollReveal<HTMLDivElement>();
  const revealRef = useGsapReveal<HTMLDivElement>({
    selector: ".news-item",
    stagger: 0.06,
    deps: [locale],
  });

  const {
    ref: trackRef,
    atStart,
    atEnd,
    scrollByCard,
  } = useCarousel<HTMLDivElement>(".news-item");

  // Solo las notas del idioma actual: mezclar idiomas en la misma pagina es
  // peor que no mostrar la seccion.
  const localePosts = postsByLang(locale);
  if (localePosts.length === 0) return null;

  return (
    <section className="news-section" aria-labelledby="novedades-titulo">
      <div className="news-section__content">
        <div className="news-section__header" ref={headerRef}>
          <div>
            <p className="news-section__eyebrow">{t.news.eyebrow}</p>
            <h2 className="news-section__title" id="novedades-titulo">
              {t.news.title}
            </h2>
          </div>

          <div className="news-section__actions">
            <button
              type="button"
              className="news-section__arrow"
              onClick={() => scrollByCard(-1)}
              disabled={atStart}
              aria-label={t.news.prev}
            >
              ←
            </button>
            <button
              type="button"
              className="news-section__arrow"
              onClick={() => scrollByCard(1)}
              disabled={atEnd}
              aria-label={t.news.next}
            >
              →
            </button>
            <Link to={path("news")} className="news-section__all">
              {t.news.seeAll}
            </Link>
          </div>
        </div>

        <div ref={revealRef}>
          {/* tabIndex 0 + role region: la lista se puede recorrer con el
              teclado, no solo con los botones o el dedo. */}
          <div
            className="news-section__track"
            ref={trackRef}
            tabIndex={0}
            role="region"
            aria-label={t.news.carousel}
          >
            {localePosts.map((post) => (
              <Link
                key={post.slug}
                to={`${path("news")}/${post.slug}`}
                className="news-item"
              >
                <div className="news-item__meta">
                  <span className="news-item__tag">{post.tag}</span>
                  <time dateTime={post.date} className="news-item__date">
                    {formatPostDate(post.date, locale)}
                  </time>
                </div>

                <h3 className="news-item__title">{post.title}</h3>
                <p className="news-item__summary">{post.summary}</p>

                <span className="news-item__cta" aria-hidden="true">
                  {t.news.read}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default NewsSection;
